import React, { useEffect, useMemo, useState } from "react";
import {
  Layers3,
  Building2,
  MapPin,
  Tags,
  Plus,
  Pencil,
  Trash2,
  Landmark,
  Wallet,
  BriefcaseBusiness,
  House,
  ShieldCheck,
  Building,
  ChartNoAxesCombined,
  Home,
  MapPinned,
  TriangleAlert,
} from "lucide-react";
import { api } from "../lib/api.js";
import { Card, Modal, Pill, Empty } from "../components/UI.jsx";

const iconChoices = [
  ["landmark", "Bank", Landmark],
  ["building", "Building", Building],
  ["chart", "Broker", ChartNoAxesCombined],
  ["wallet", "Wallet", Wallet],
  ["vault", "Safe", ShieldCheck],
  ["home", "Property", Home],
  ["briefcase", "Business", BriefcaseBusiness],
  ["pin", "Place", MapPinned],
];
const iconMap = Object.fromEntries(iconChoices.map(([k, , I]) => [k, I]));
const typeIcon = {
  bank: Landmark,
  broker: BriefcaseBusiness,
  cash: Wallet,
  safe: ShieldCheck,
  wallet: Wallet,
  real_estate: House,
  other: MapPin,
};
const defaultIconForType = {
  bank: "landmark",
  broker: "chart",
  cash: "wallet",
  safe: "vault",
  wallet: "wallet",
  real_estate: "home",
  other: "pin",
};
const blank = {
  name: "",
  isActive: true,
  centerType: "other",
  level: 1,
  parentId: "",
  financialType: "expense",
  validDirection: "both",
  incomeQuadrant: "",
  locationType: "bank",
  icon: "landmark",
  costCenterId: "",
  color: "#4d815c",
};
const treatments = {
  active_income: "Active/work income",
  passive_income: "Passive/investment income",
  expense: "Expense",
  investment: "Investment purchase or sale",
  transfer: "Transfer between balances",
  excluded: "Excluded from reports",
};
const quadrants = {
  employee: "Employee",
  self_employed: "Self-employed",
  business_owner: "Business owner",
  investor: "Investor",
  unclassified: "Unclassified",
};

export default function Organize() {
  const [cfg, setCfg] = useState(null),
    [tab, setTab] = useState("classifications"),
    [modal, setModal] = useState(null),
    [editing, setEditing] = useState(null),
    [f, setF] = useState(blank),
    [error, setError] = useState(""),
    [collapsed, setCollapsed] = useState(new Set()),
    [categoryDelete, setCategoryDelete] = useState(null),
    [replacementId, setReplacementId] = useState(""),
    [mergeCostCenter, setMergeCostCenter] = useState(null),
    [mergeCostCenterTarget, setMergeCostCenterTarget] = useState("");
  const load = () =>
    api("/config")
      .then(setCfg)
      .catch((e) => setError(e.message));
  useEffect(() => {
    load();
  }, []);
  const open = (kind, item = null) => {
    setEditing(item);
    setModal(kind);
    setError("");
    setF(
      item
        ? {
            ...blank,
            name: kind === "category" ? item.custom_name : item.name,
            isActive: item.is_active !== 0,
            centerType: item.center_type || "other",
            level: item.level || 1,
            parentId: item.parent_id ? String(item.parent_id) : "",
            financialType: item.default_financial_type || "expense",
            validDirection: item.valid_direction || "both",
            incomeQuadrant: item.income_quadrant || "",
            locationType: item.location_type || "bank",
            icon: item.icon || defaultIconForType[item.location_type] || "pin",
            costCenterId: item.default_cost_center_id
              ? String(item.default_cost_center_id)
              : "",
            color: item.color || "#4d815c",
          }
        : { ...blank },
    );
  };
  const done = () => {
    setModal(null);
    setEditing(null);
    load();
    window.dispatchEvent(new Event("pisto:data-changed"));
  };
  const save = async (e) => {
    e.preventDefault();
    try {
      if (modal === "category")
        await api(editing ? `/categories/${editing.id}` : "/categories", {
          method: editing ? "PUT" : "POST",
          body: {
            name: f.name,
            level: Number(f.level),
            parentId: f.parentId || null,
            financialType: f.financialType,
            validDirection: f.validDirection,
            incomeQuadrant: f.incomeQuadrant || null,
            isActive: f.isActive,
          },
        });
      if (modal === "cost")
        await api(editing ? `/cost-centers/${editing.id}` : "/cost-centers", {
          method: editing ? "PUT" : "POST",
          body: {
            name: f.name,
            centerType: f.centerType,
            color: f.color,
            isActive: f.isActive,
          },
        });
      if (modal === "location")
        await api(editing ? `/locations/${editing.id}` : "/locations", {
          method: editing ? "PUT" : "POST",
          body: {
            name: f.name,
            locationType: f.locationType,
            icon: f.icon,
            costCenterId: f.costCenterId || null,
            isActive: f.isActive,
          },
        });
      if (modal === "tag")
        await api(editing ? `/tags/${editing.id}` : "/tags", {
          method: editing ? "PUT" : "POST",
          body: { name: f.name, isActive: f.isActive },
        });
      done();
    } catch (e) {
      setError(e.message);
    }
  };
  const toggleSimple = async (kind, item) => {
    const next = !(item.is_active !== 0);
    try {
      if (kind === "cost")
        await api(`/cost-centers/${item.id}`, {
          method: "PUT",
          body: {
            name: item.name,
            centerType: item.center_type,
            color: item.color,
            isActive: next,
          },
        });
      if (kind === "location")
        await api(`/locations/${item.id}`, {
          method: "PUT",
          body: {
            name: item.name,
            locationType: item.location_type,
            icon: item.icon,
            costCenterId: item.default_cost_center_id || null,
            isActive: next,
          },
        });
      if (kind === "tag")
        await api(`/tags/${item.id}`, {
          method: "PUT",
          body: { name: item.name, isActive: next },
        });
      load();
    } catch (e) {
      setError(e.message);
    }
  };
  const removeCost = async (item) => {
    if (!confirm(`Delete cost center “${item.name}”?`)) return;
    try {
      await api(`/cost-centers/${item.id}`, { method: "DELETE" });
      load();
    } catch (e) {
      setError(e.message);
    }
  };
  const removeTag = async (item) => {
    if (
      !confirm(
        `Delete tag “${item.name}”? It will be removed from movements, but no movement will be deleted.`,
      )
    )
      return;
    try {
      await api(`/tags/${item.id}`, { method: "DELETE" });
      load();
    } catch (e) {
      setError(e.message);
    }
  };
  const performCostCenterMerge = async () => {
    if (!mergeCostCenterTarget) return;
    try {
      await api(`/cost-centers/${mergeCostCenter.id}/merge`, { method:"POST", body:{ targetId:Number(mergeCostCenterTarget) } });
      setMergeCostCenter(null); setMergeCostCenterTarget(""); load();
    } catch (e) { setError(e.message); }
  };
  const removeCategory = async () => {
    try {
      await api(`/categories/${categoryDelete.id}`, {
        method: "DELETE",
        body: replacementId ? { replacementId: Number(replacementId) } : {},
      });
      setCategoryDelete(null);
      setReplacementId("");
      setError("");
      load();
    } catch (e) {
      setError(e.message);
    }
  };
  const hierarchy = useMemo(() => {
    if (!cfg) return [];
    return cfg.costCenters.map((cc) => ({
      cc,
      locations: (cfg.locations || [])
        .filter((l) => String(l.default_cost_center_id || "") === String(cc.id))
        .map((loc) => ({
          loc,
          accounts: (cfg.accounts || []).filter(
            (a) => String(a.location_id || "") === String(loc.id),
          ),
        })),
    }));
  }, [cfg]);
  if (!cfg) return <div className="loading">Loading organization…</div>;
  const toggle = (k) =>
    setCollapsed((s) => {
      const n = new Set(s);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Organize</h1>
          <p>
            Classifications and the structure that connects cost centers, places
            and accounts.
          </p>
        </div>
      </div>
      {error && <div className="error">{error}</div>}
      <div className="tabs organize-tabs">
        <button
          className={tab === "classifications" ? "active" : ""}
          onClick={() => setTab("classifications")}
        >
          Classifications
        </button>
        <button
          className={tab === "structure" ? "active" : ""}
          onClick={() => setTab("structure")}
        >
          Cost centers · places · accounts
        </button>
      </div>
      {tab === "classifications" ? (
        <div className="two-col">
          <Card className="span-wide">
            <div className="section-head">
              <div>
                <Layers3 />
                <h2>Categories</h2>
              </div>
              <button
                className="icon"
                title="New category"
                onClick={() => open("category")}
              >
                <Plus />
              </button>
            </div>
            <p className="card-help">
              The hierarchy says what a movement is. Direction, financial
              treatment and income quadrant are independent settings.
            </p>
            <div className="category-tree">
              {cfg.categories.map((x) => (
                <div key={x.id} style={{ paddingLeft: (x.level - 1) * 18 }}>
                  <span>
                    {x.level > 1 ? "↳ " : ""}
                    <b>{x.custom_name}</b>
                  </span>
                  <span className="row-actions">
                    <Pill>{x.valid_direction || "both"}</Pill>
                    <Pill>
                      {treatments[x.default_financial_type] || "Custom"}
                    </Pill>
                    {x.income_quadrant && (
                      <Pill>{quadrants[x.income_quadrant]}</Pill>
                    )}
                    <button
                      className="icon"
                      title="Edit"
                      onClick={() => open("category", x)}
                    >
                      <Pencil size={15} />
                    </button>
                    <button
                      className="icon danger"
                      title="Delete or merge"
                      onClick={() => {
                        setCategoryDelete(x);
                        setReplacementId("");
                        setError("");
                      }}
                    >
                      <Trash2 size={15} />
                    </button>
                  </span>
                </div>
              ))}
            </div>
          </Card>
          <Card>
            <h3>How classification works</h3>
            <p className="card-help">
              <b>Category</b> describes purpose, such as Investments → Stocks →
              Dividends. <b>Direction</b> allows income, expense or both.{" "}
              <b>Financial treatment</b> controls cash-flow reports. Net worth
              comes from balances—not category names.
            </p>
          </Card>
          <Card>
            <div className="section-head">
              <div>
                <Tags />
                <h2>Tags</h2>
              </div>
              <button className="icon" onClick={() => open("tag")}>
                <Plus />
              </button>
            </div>
            {!(cfg.tags || []).length ? (
              <Empty
                title="No configured tags"
                text="Create reusable tags here."
              />
            ) : (
              <div className="simple-list">
                {cfg.tags.map((x) => (
                  <div key={x.id}>
                    <b>{x.name}</b>
                    <span className="row-actions">
                      <Pill onClick={() => toggleSimple("tag", x)}>
                        {x.is_active ? "Active" : "Hidden"}
                      </Pill>
                      <button className="icon" onClick={() => open("tag", x)}>
                        <Pencil size={15} />
                      </button>
                      <button className="icon danger" title="Delete tag" onClick={() => removeTag(x)}>
                        <Trash2 size={15} />
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      ) : (
        <div className="structure-layout">
          <Card>
            <div className="section-head">
              <div>
                <Building2 />
                <h2>Cost centers</h2>
              </div>
              <button className="icon" onClick={() => open("cost")}>
                <Plus />
              </button>
            </div>
            <div className="simple-list">
              {cfg.costCenters.map((x) => (
                <div key={x.id}>
                  <span>
                    <b>{x.name}</b>
                    <small>{x.center_type}</small>
                  </span>
                  <span className="row-actions">
                    <Pill onClick={() => toggleSimple("cost", x)}>
                      {x.is_active ? "Active" : "Hidden"}
                    </Pill>
                    <button className="icon" onClick={() => open("cost", x)}>
                      <Pencil size={15} />
                    </button>
                    <button className="icon" title="Merge into another cost center" onClick={() => { setMergeCostCenter(x); setMergeCostCenterTarget(""); }}>
                      <Layers3 size={15}/>
                    </button>
                    <button className="icon" onClick={() => removeCost(x)}>
                      <Trash2 size={15} />
                    </button>
                  </span>
                </div>
              ))}
            </div>
          </Card>
          <Card>
            <div className="section-head">
              <div>
                <MapPin />
                <h2>Places</h2>
              </div>
              <button className="icon" onClick={() => open("location")}>
                <Plus />
              </button>
            </div>
            <div className="simple-list">
              {(cfg.locations || []).map((x) => {
                const I =
                  iconMap[x.icon] || typeIcon[x.location_type] || MapPin;
                return (
                  <div key={x.id}>
                    <span>
                      <b>
                        <I size={14} /> {x.name}
                      </b>
                      <small>{x.location_type}</small>
                    </span>
                    <span className="row-actions">
                      <Pill onClick={() => toggleSimple("location", x)}>
                        {x.is_active ? "Active" : "Hidden"}
                      </Pill>
                      <button
                        className="icon"
                        onClick={() => open("location", x)}
                      >
                        <Pencil size={15} />
                      </button>
                    </span>
                  </div>
                );
              })}
            </div>
          </Card>
          <Card className="span-wide">
            <h2>Structure preview</h2>
            {hierarchy.map(({ cc, locations }) => (
              <div className="tree-block" key={cc.id}>
                <button
                  className="tree-toggle"
                  onClick={() => toggle("cc" + cc.id)}
                >
                  <b>{cc.name}</b>
                  <span>{collapsed.has("cc" + cc.id) ? "＋" : "−"}</span>
                </button>
                {!collapsed.has("cc" + cc.id) && (
                  <div className="tree-children">
                    {locations.map(({ loc, accounts }) => (
                      <div key={loc.id}>
                        <strong>{loc.name}</strong>
                        <div className="tree-accounts">
                          {accounts.map((a) => {
                            const mismatch =
                              a.cost_center_id &&
                              String(a.cost_center_id) !==
                                String(loc.default_cost_center_id);
                            return (
                              <span
                                className={
                                  mismatch
                                    ? "tree-account mismatch"
                                    : "tree-account"
                                }
                                key={a.id}
                              >
                                {mismatch && <TriangleAlert size={12} />}{" "}
                                {a.name} · {a.currency}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </Card>
        </div>
      )}
      {modal && (
        <Modal
          title={`${editing ? "Edit" : "New"} ${modal === "cost" ? "cost center" : modal === "location" ? "place" : modal}`}
          screenId="D0451"
          onClose={() => setModal(null)}
        >
          <form onSubmit={save} className="form-grid">
            {modal === "category" ? (
              <>
                <label>
                  Name
                  <input
                    required
                    value={f.name}
                    onChange={(e) => setF({ ...f, name: e.target.value })}
                  />
                </label>
                <label>
                  Level
                  <select
                    value={f.level}
                    onChange={(e) =>
                      setF({
                        ...f,
                        level: Number(e.target.value),
                        parentId: "",
                      })
                    }
                  >
                    <option value="1">1 · Main</option>
                    <option value="2">2 · Category</option>
                    <option value="3">3 · Subcategory</option>
                  </select>
                </label>
                <label>
                  Parent category
                  <select
                    value={f.parentId}
                    onChange={(e) => setF({ ...f, parentId: e.target.value })}
                  >
                    <option value="">None</option>
                    {cfg.categories
                      .filter(
                        (x) =>
                          x.id !== editing?.id &&
                          x.level === Number(f.level) - 1,
                      )
                      .map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.custom_name}
                        </option>
                      ))}
                  </select>
                </label>
                <label>
                  Direction
                  <select
                    value={f.validDirection}
                    onChange={(e) =>
                      setF({ ...f, validDirection: e.target.value })
                    }
                  >
                    <option value="both">Income & expense</option>
                    <option value="income">Income only</option>
                    <option value="expense">Expense only</option>
                  </select>
                </label>
                <label>
                  Financial treatment
                  <select
                    value={f.financialType}
                    onChange={(e) =>
                      setF({ ...f, financialType: e.target.value })
                    }
                  >
                    {Object.entries(treatments).map(([v, l]) => (
                      <option key={v} value={v}>
                        {l}
                      </option>
                    ))}
                  </select>
                  <small>Controls cash-flow reports, not net worth.</small>
                </label>
                <label>
                  Income quadrant default
                  <select
                    value={f.incomeQuadrant}
                    onChange={(e) =>
                      setF({ ...f, incomeQuadrant: e.target.value })
                    }
                  >
                    <option value="">None / not income</option>
                    {Object.entries(quadrants).map(([v, l]) => (
                      <option key={v} value={v}>
                        {l}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            ) : modal === "cost" ? (
              <>
                <label>
                  Name
                  <input
                    required
                    value={f.name}
                    onChange={(e) => setF({ ...f, name: e.target.value })}
                  />
                </label>
                <label>
                  Type
                  <select
                    value={f.centerType}
                    onChange={(e) => setF({ ...f, centerType: e.target.value })}
                  >
                    {["personal", "company", "property", "other"].map((x) => (
                      <option key={x}>{x}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Color
                  <input type="color" value={f.color} onChange={(e) => setF({ ...f, color:e.target.value })}/>
                </label>
              </>
            ) : modal === "location" ? (
              <>
                <label>
                  Name
                  <input
                    required
                    value={f.name}
                    onChange={(e) => setF({ ...f, name: e.target.value })}
                  />
                </label>
                <label>
                  Place type
                  <select
                    value={f.locationType}
                    onChange={(e) =>
                      setF({
                        ...f,
                        locationType: e.target.value,
                        icon: defaultIconForType[e.target.value] || "pin",
                      })
                    }
                  >
                    {[
                      "bank",
                      "broker",
                      "cash",
                      "safe",
                      "wallet",
                      "real_estate",
                      "other",
                    ].map((x) => (
                      <option key={x}>{x.replaceAll("_", " ")}</option>
                    ))}
                  </select>
                </label>
                <label className="span2">
                  Default cost center
                  <select
                    value={f.costCenterId}
                    onChange={(e) =>
                      setF({ ...f, costCenterId: e.target.value })
                    }
                  >
                    <option value="">No default</option>
                    {cfg.costCenters
                      .filter((x) => x.is_active !== 0)
                      .map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.name}
                        </option>
                      ))}
                  </select>
                </label>
                <div className="span2">
                  <span className="field-label">Icon</span>
                  <div className="icon-choice-grid">
                    {iconChoices.map(([key, label, I]) => (
                      <button
                        type="button"
                        className={
                          f.icon === key ? "icon-choice active" : "icon-choice"
                        }
                        key={key}
                        onClick={() => setF({ ...f, icon: key })}
                      >
                        <I />
                        <small>{label}</small>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <label className="span2">
                Tag name
                <input
                  required
                  value={f.name}
                  onChange={(e) => setF({ ...f, name: e.target.value })}
                />
              </label>
            )}
            <label className="check span2">
              <input
                type="checkbox"
                checked={f.isActive}
                onChange={(e) => setF({ ...f, isActive: e.target.checked })}
              />{" "}
              Active
            </label>
            <button className="primary span2 modal-submit">Save</button>
          </form>
        </Modal>
      )}
      {categoryDelete && (
        <Modal
          title={`Delete ${categoryDelete.custom_name}?`}
          onClose={() => setCategoryDelete(null)}
        >
          <p>
            If this category has been used, choose a replacement. Pisto will
            merge its movement assignments safely.
          </p>
          <label>
            Replacement category
            <select
              value={replacementId}
              onChange={(e) => setReplacementId(e.target.value)}
            >
              <option value="">None — only delete if unused</option>
              {cfg.categories
                .filter((x) => x.id !== categoryDelete.id && x.is_active !== 0)
                .map((x) => (
                  <option key={x.id} value={x.id}>
                    {"—".repeat(Math.max(0, x.level - 1))} {x.custom_name}
                  </option>
                ))}
            </select>
          </label>
          <button className="danger modal-submit" onClick={removeCategory}>
            Delete category
          </button>
        </Modal>
      )}
      {mergeCostCenter && (
        <Modal title={`Merge ${mergeCostCenter.name}`} onClose={() => setMergeCostCenter(null)}>
          <p>Every movement, account, place, asset, project, goal, budget and recurring payment using this cost center will be reassigned.</p>
          <label>Destination cost center<select value={mergeCostCenterTarget} onChange={(e) => setMergeCostCenterTarget(e.target.value)}><option value="">Choose destination…</option>{cfg.costCenters.filter((center) => center.id !== mergeCostCenter.id).map((center) => <option key={center.id} value={center.id}>{center.name}</option>)}</select></label>
          <div className="modal-actions"><button className="secondary" onClick={() => setMergeCostCenter(null)}>Cancel</button><button className="primary" disabled={!mergeCostCenterTarget} onClick={performCostCenterMerge}>Merge cost centers</button></div>
        </Modal>
      )}
    </>
  );
}
